import { useCallback, useEffect, useState } from "react";

import { deleteCollaborator, updateCollaborator } from "../../../../../service/organisation/OrganisationService";
import {
  lookUp,
  SearchCollaboratorsResponse,
  searchDevelopers,
  searchProjects,
  SearchProjectsResponse,
} from "../../../../../service/query";
import { ResultData, ServiceError, Status } from "../../../../../service/Shared";
import { DataGridCursorDataLoadEventHandler } from "../../../../../widget";
import { useCompanyAssociations } from "./useCompanyAssociations";
import { useLinkProjectModal } from "./useLinkProjectModal";

interface UseCompanyDetailsModalProps {
  companyUuid: string | null;
  show: boolean;
  onClose: () => void;
  onDeleted?: () => void;
  onCompanyRefetch?: () => void;
  fetchCompanyByUuid: (companyUuid: string) => Promise<SearchCollaboratorsResponse["results"][number] | null>;
}

interface UseCompanyDetailsModalReturn {
  company: SearchCollaboratorsResponse["results"][number] | null;
  isCompanyLoading: boolean;
  companyErrorCode: string | null;
  requestCompanyRefetch: () => void;
  countryName: string | null;
  linkedOrganisationName: string | null;
  isDeleting: boolean;
  showLinkProjectModal: boolean;
  linkProjectErrors: ServiceError[];
  isLinkProjectSaving: boolean;
  projectOptions: ReturnType<typeof useLinkProjectModal>["projectOptions"];
  projectUuid: ReturnType<typeof useLinkProjectModal>["projectUuid"];
  companyRole: ReturnType<typeof useLinkProjectModal>["companyRole"];
  setCompanyRole: ReturnType<typeof useLinkProjectModal>["setCompanyRole"];
  setProjectUuid: ReturnType<typeof useLinkProjectModal>["setProjectUuid"];
  fetchProjects: ReturnType<typeof useLinkProjectModal>["fetchProjects"];
  associatedProjects: SearchCollaboratorsResponse["results"][number]["associatedProjects"];
  associatedProjectRows: ReturnType<typeof useCompanyAssociations>["associatedProjectRows"];
  displaySource: string;
  setDisplaySource: (value: string) => void;
  rowVersion: number;
  handleDelete: () => Promise<void>;
  handleOpenLinkProject: () => void;
  handleCloseLinkProject: () => void;
  handleCreateLinkProject: () => Promise<void>;
  handleRemoveProject: (projectUuid: string) => Promise<void>;
  dataGridColumns: ReturnType<typeof useCompanyAssociations>["dataGridColumns"];
  onAssociatedProjectRowsGridChange: ReturnType<typeof useCompanyAssociations>["onAssociatedProjectRowsGridChange"];
  onKanaHubAssociatedProjectRowsGridChange: DataGridCursorDataLoadEventHandler;
  setRowVersion: (rowVersion: number) => void;
}

export const useCompanyDetailsModal = ({
  companyUuid,
  show,
  onClose,
  onDeleted,
  onCompanyRefetch,
  fetchCompanyByUuid,
}: UseCompanyDetailsModalProps): UseCompanyDetailsModalReturn => {
  const [companyRefreshKey, setCompanyRefreshKey] = useState(0);
  const requestCompanyRefetch = useCallback(() => {
    setCompanyRefreshKey((prev) => prev + 1);
  }, []);
  const [company, setCompany] = useState<SearchCollaboratorsResponse["results"][number] | null>(null);
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);
  const [companyErrorCode, setCompanyErrorCode] = useState<string | null>(null);
  const [countryName, setCountryName] = useState<string | null>(null);
  const [linkedOrganisationName, setLinkedOrganisationName] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showLinkProjectModal, setShowLinkProjectModal] = useState(false);
  const [linkProjectErrors, setLinkProjectErrors] = useState<ServiceError[]>([]);
  const [isLinkProjectSaving, setIsLinkProjectSaving] = useState(false);
  const [displaySource, setDisplaySource] = useState<string>("crmLinked");
  const [developerUuid, setDeveloperUuid] = useState<string | null>(null);
  const [associatedProjects, setAssociatedProjects] = useState<
    SearchCollaboratorsResponse["results"][number]["associatedProjects"]
  >(company?.associatedProjects ?? []);
  const [rowVersion, setRowVersion] = useState(company?.rowVersion ?? 0);
  const {
    projectOptions,
    projectUuid,
    selectedProject,
    companyRole,
    setCompanyRole,
    setProjectUuid,
    fetchProjects,
    reset,
  } = useLinkProjectModal({ show: showLinkProjectModal });
  const { handleRemoveProject, dataGridColumns, onAssociatedProjectRowsGridChange, associatedProjectRows } =
    useCompanyAssociations({
      company,
      associatedProjects,
      rowVersion,
      setRowVersion,
      onCompanyRefetch: onCompanyRefetch ?? requestCompanyRefetch,
    });

  useEffect(() => {
    let isActive = true;
    const fetchCountryName = async (): Promise<void> => {
      const code = company?.addressCountryCode;
      if (!code) {
        if (isActive) setCountryName(null);
        return;
      }

      const countriesRes = await lookUp({ type: "Country", sort: [{ key: "valueString", direction: "asc" }] });
      if (!isActive) return;

      if (countriesRes.status === Status.Success && countriesRes.data) {
        const match = countriesRes.data.find((country) => country.key === code);
        setCountryName(match?.valueString ?? code);
        return;
      }

      setCountryName(code);
    };

    fetchCountryName();

    return () => {
      isActive = false;
    };
  }, [company?.addressCountryCode]);

  useEffect(() => {
    let isActive = true;
    const fetchLinkedOrganisationName = async (): Promise<void> => {
      const organisationUuid = company?.organisationUuid;
      if (!organisationUuid) {
        if (isActive) setLinkedOrganisationName(null);
        if (isActive) setDeveloperUuid(null);
        return;
      }

      const orgRes = await searchDevelopers({
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 1,
        },
        filter: {
          results: {
            parentOrganisation: {
              uuid: {
                operator: "eq",
                value: organisationUuid,
              },
            },
          },
        },
        excludedProjectUuid: null,
      });

      if (!isActive) return;

      if (orgRes.status === Status.Success && orgRes.data?.results?.length) {
        const match = orgRes.data.results[0];
        setLinkedOrganisationName(match.parentOrganisation.displayName ?? match.displayName ?? organisationUuid);
        setDeveloperUuid(match.uuid ?? null);
        return;
      }

      setLinkedOrganisationName(organisationUuid);
      setDeveloperUuid(null);
    };

    fetchLinkedOrganisationName();

    return () => {
      isActive = false;
    };
  }, [company?.organisationUuid]);

  useEffect(() => {
    let isActive = true;
    if (!show || !companyUuid) {
      setCompany(null);
      setIsCompanyLoading(false);
      setCompanyErrorCode(null);
      return () => {
        isActive = false;
      };
    }

    setCompany(null);
    setIsCompanyLoading(true);
    setCompanyErrorCode(null);

    fetchCompanyByUuid(companyUuid)
      .then((result) => {
        if (!isActive) return;
        if (result) {
          setCompany(result);
        } else {
          setCompany(null);
          setCompanyErrorCode("ERROR_APPLICATION");
        }
      })
      .catch(() => {
        if (!isActive) return;
        setCompany(null);
        setCompanyErrorCode("ERROR_APPLICATION");
      })
      .finally(() => {
        if (isActive) setIsCompanyLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [companyRefreshKey, companyUuid, fetchCompanyByUuid, show]);

  useEffect(() => {
    if (!company) return;
    setAssociatedProjects(company.associatedProjects ?? []);
    setRowVersion(company.rowVersion);
  }, [company?.rowVersion, company?.uuid]);

  const handleDelete = useCallback(async (): Promise<void> => {
    if (!company || isDeleting) return;
    setIsDeleting(true);

    const res = await deleteCollaborator({
      collaboratorUuid: company.uuid,
      rowVersion: company.rowVersion,
    });

    setIsDeleting(false);

    if (res.status === Status.Success) {
      onDeleted?.();
      onClose();
    }
  }, [company, isDeleting, onClose, onDeleted]);

  const handleOpenLinkProject = useCallback((): void => {
    setShowLinkProjectModal(true);
  }, []);

  const handleCloseLinkProject = useCallback((): void => {
    reset();
    setLinkProjectErrors([]);
    setShowLinkProjectModal(false);
  }, [reset]);

  const buildAssociatedProject = (
    project: SearchProjectsResponse["results"][number] | null,
    role: string
  ): SearchCollaboratorsResponse["results"][number]["associatedProjects"][number] | null => {
    if (!project) return null;
    return {
      areaHa: project.areaNetHa !== null ? String(project.areaNetHa) : null,
      developerDisplayName: project.developer?.displayName ?? null,
      displayName: project.displayName,
      groups: [],
      locationCoordinates: project.locationCoordinates ?? null,
      phase: project.phase ?? null,
      projectTypeDisplayName: project.projectType?.displayName ?? "",
      region: null,
      role,
      standardDisplayName: project.standard?.displayName ?? null,
      unitQuantityAvailable: project.cachedUnitAvailableForSale,
      uuid: project.uuid,
      verifierDisplayName: project.validator?.displayName ?? null,
    };
  };

  const handleCreateLinkProject = useCallback(async (): Promise<void> => {
    if (!company || !projectUuid || !companyRole.trim()) return;
    setIsLinkProjectSaving(true);
    setLinkProjectErrors([]);

    const trimmedRole = companyRole.trim();
    const nextProject = buildAssociatedProject(selectedProject, trimmedRole);
    if (!nextProject) {
      setIsLinkProjectSaving(false);
      return;
    }

    const associationPayload = [
      {
        endDate: null,
        objectType: "Project",
        objectUuid: nextProject.uuid,
        role: nextProject.role,
      },
    ];

    const response = await updateCollaborator({
      addressCity: company.addressCity,
      addressCountryCode: company.addressCountryCode,
      addressLine1: company.addressLine1,
      addressLine2: company.addressLine2,
      addressPostcode: company.addressPostcode,
      addressRegion: company.addressRegion,
      associations: associationPayload,
      collaboratorType: company.collaboratorType,
      description: company.description,
      email: company.email,
      externalReference: company.externalReference,
      name: company.name,
      organisationUuid: company.organisationUuid ?? "",
      phone: company.phone,
      rowVersion,
      uuid: company.uuid,
      website: company.website,
    });

    if (response.status === Status.Error) {
      setLinkProjectErrors(response.errors || []);
      setIsLinkProjectSaving(false);
      return;
    }

    if (response.status === Status.Success && response.data?.rowVersion) {
      setRowVersion(response.data.rowVersion);
    }
    (onCompanyRefetch ?? requestCompanyRefetch)();
    setIsLinkProjectSaving(false);
    handleCloseLinkProject();
  }, [
    company,
    companyRole,
    handleCloseLinkProject,
    onCompanyRefetch,
    projectUuid,
    requestCompanyRefetch,
    rowVersion,
    selectedProject,
  ]);

  const onKanaHubAssociatedProjectRowsGridChange: DataGridCursorDataLoadEventHandler = useCallback(
    async ({ paging = { pageSize: 20, afterCursor: null, beforeCursor: null } }) => {
      if (!developerUuid) {
        return {
          resultData: [],
          paging: {
            startCursor: "",
            endCursor: "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: 0,
            totalCount: 0,
          },
        };
      }

      const response = await searchProjects({
        paging: {
          afterCursor: paging?.afterCursor ?? null,
          beforeCursor: paging?.beforeCursor ?? null,
          limit: paging?.pageSize ?? 20,
        },
        sort: [{ key: "results.displayName", direction: "asc" }],
        filter: {
          results: {
            developer: {
              uuid: {
                operator: "eq",
                value: developerUuid,
              },
            },
          },
        },
      });

      if (response.status !== Status.Success || !response.data?.results) {
        return {
          resultData: [],
          paging: {
            startCursor: "",
            endCursor: "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: 0,
            totalCount: 0,
          },
        };
      }

      const resultData: ResultData[] = response.data.results.map((project) => ({
        uuid: project.uuid,
        displayName: project.displayName ?? "",
        landholding: project.groups[0]?.groupName ?? "",
        role: "Developer",
        projectTypeDisplayName: project.projectType?.displayName ?? "",
        area: project.areaNetHa !== null ? `${project.areaNetHa} ha` : "",
        phase: project.phase ?? "",
        availableUnits: project.cachedUnitAvailableForSale,
        standardDisplayName: project.standard?.displayName ?? "",
      }));

      return {
        resultData,
        paging: {
          startCursor: response.data.paging.startCursor,
          endCursor: response.data.paging.endCursor,
          hasNextPage: response.data.paging.hasNextPage,
          hasPreviousPage: response.data.paging.hasPreviousPage,
          pageSize: paging?.pageSize ?? 20,
          totalCount: response.data.paging.total,
        },
      };
    },
    [developerUuid]
  );

  return {
    company,
    isCompanyLoading,
    companyErrorCode,
    requestCompanyRefetch,
    countryName,
    linkedOrganisationName,
    isDeleting,
    showLinkProjectModal,
    linkProjectErrors,
    isLinkProjectSaving,
    displaySource,
    setDisplaySource,
    projectOptions,
    projectUuid,
    companyRole,
    setCompanyRole,
    setProjectUuid,
    fetchProjects,
    associatedProjects,
    associatedProjectRows,
    rowVersion,
    handleDelete,
    handleOpenLinkProject,
    handleCloseLinkProject,
    handleCreateLinkProject,
    handleRemoveProject,
    dataGridColumns,
    onAssociatedProjectRowsGridChange,
    onKanaHubAssociatedProjectRowsGridChange,
    setRowVersion,
  };
};
